import React, { useRef, useState } from 'react';
import { formatCurrency } from '../../utils/formatters';

/**
 * Modal para asignar uno o varios servicio·instancia a uno o varios
 * clientes/proyectos. Alternativa al drag & drop: se abre al hacer clic en una
 * fila de la columna 2, o al pulsar "Asignar a clientes" con varios marcados.
 * El costo de cada servicio se reparte equitativamente (100/N %) entre los
 * clientes seleccionados.
 */
export const ServicioAsignarModal = ({
  servicios = [],
  clientes,
  asignados = [],
  onConfirm,
  onClose
}) => {
  const [selected, setSelected] = useState(() => new Set(asignados));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const n = selected.size;
  const pctCadaUno = n > 0 ? Math.floor((100 / n) * 100) / 100 : 0;
  const todosSeleccionados = clientes.length > 0 && n === clientes.length;
  const algunoSeleccionado = n > 0 && n < clientes.length;

  // Checkbox indeterminado cuando la selección es parcial
  const selectAllRef = useRef(null);
  if (selectAllRef.current) selectAllRef.current.indeterminate = algunoSeleccionado;

  const toggleTodos = () => {
    setSelected(todosSeleccionados ? new Set() : new Set(clientes.map((c) => c.id)));
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selected));
  };

  const esBulk = servicios.length > 1;
  const totalGeneral = servicios.reduce((s, x) => s + (x.total || 0), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </span>
          <div className="modal-header-text">
            <h3 className="modal-title">
              {esBulk ? `Asignar ${servicios.length} servicios` : 'Asignar servicio'}
            </h3>
            <p className="modal-subtitle">
              {esBulk
                ? 'Todos se repartirán entre los mismos clientes.'
                : 'Reparte el costo entre uno o varios clientes.'}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="asignar-modal-servicio">
            <div className="asignar-modal-servicio-info">
              <span className="asignar-modal-servicio-nombre">
                {esBulk
                  ? `${servicios.length} servicios seleccionados`
                  : servicios[0].service}
              </span>
              <span className="asignar-modal-servicio-instancia">
                {esBulk ? 'Costo total combinado' : servicios[0].instance}
              </span>
            </div>
            <span className="asignar-modal-servicio-total">
              {formatCurrency(esBulk ? totalGeneral : servicios[0].total)}
            </span>
          </div>

          {esBulk && (
            <ul className="asignar-modal-servicios-mini">
              {servicios.map((s, i) => (
                <li key={i}>
                  <span className="asignar-modal-mini-nombre">
                    {s.service}
                    <span className="asignar-modal-mini-inst"> · {s.instance}</span>
                  </span>
                  <span className="asignar-modal-mini-total">{formatCurrency(s.total)}</span>
                </li>
              ))}
            </ul>
          )}

          <p className="asignar-modal-hint">
            {n > 0 ? (
              <>
                {esBulk ? 'En cada servicio, cada' : 'Cada'} uno de los {n} cliente
                {n === 1 ? '' : 's'} seleccionado{n === 1 ? '' : 's'} recibe{' '}
                <strong>{pctCadaUno}%</strong> del costo.
              </>
            ) : (
              'Selecciona los clientes entre los que se reparte este costo.'
            )}
          </p>

          <ul className="asignar-modal-list">
            {clientes.length === 0 && (
              <li className="asignar-modal-empty">No hay clientes. Crea uno en la columna 1.</li>
            )}
            {clientes.length > 0 && (
              <li>
                <label className="asignar-modal-item asignar-modal-item-all">
                  <input
                    type="checkbox"
                    ref={selectAllRef}
                    checked={todosSeleccionados}
                    onChange={toggleTodos}
                  />
                  <span className="asignar-modal-item-name">Seleccionar todos</span>
                </label>
              </li>
            )}
            {clientes.map((c) => (
              <li key={c.id}>
                <label className="asignar-modal-item">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                  />
                  <span className="asignar-modal-item-name">{c.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div className="modal-footer">
          <button className="modal-btn modal-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="modal-btn modal-btn-primary" onClick={handleConfirm}>
            {n === 0 ? 'Quitar asignación' : `Asignar a ${n} cliente${n === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};
